import React, {useEffect, useState} from 'react';
import { useAuth } from "../../services/AuthProvider";
import {useLocation, useNavigate} from "react-router-dom";
import {Box, Input, Typography} from "@mui/material";
import styles from "../Login/Login.module.css";
import KeyIcon from "../../resources/Login/key.png";

import {
  ConfirmationCodePattern,
  UsernamePattern,
  getPasswordRecommendations,
  getPasswordStrength, PasswordPattern
} from "../../services/Util";
import {setNewPassword} from "../../services/HttpService";
import {useError} from "../../services/ErrorContext";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const SetNewPassword: React.FC = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState<string>('');
  const [inputUsername, setInputUsername] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [inputCode, setInputCode] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = React.useState(false);
  const { setError } = useError();

  useEffect(() => {
    if (isLoggedIn) {
      navigate('/dashboard');
    }

    const searchParams = new URLSearchParams(location.search);
    const usernameParam = searchParams.get('username');
    const codeParam = searchParams.get('code');
    if (usernameParam && UsernamePattern.test(decodeURIComponent(usernameParam))) {
      setUsername(decodeURIComponent(usernameParam));
    }
    if (codeParam && ConfirmationCodePattern.test(codeParam)) {
      setCode(codeParam);
    }
  }, [isLoggedIn, navigate, location]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const usernameToUse = UsernamePattern.test(username) ? username : UsernamePattern.test(inputUsername) ? inputUsername : "";
    if (!usernameToUse) {
      setError("No valid username detected, please check your link or redo the forget password process.");
      return;
    }

    const codeToUse = ConfirmationCodePattern.test(code) ? code : ConfirmationCodePattern.test(inputCode) ? inputCode : "";
    if (!codeToUse) {
      setError("No valid code detected, please check your link or redo the forget password process.");
      return;
    }

    const password = (event.target as any).password.value;
    if (!PasswordPattern.test(password)) {
      setError("Please input a stronger password.");
      return;
    }

    try {
      await setNewPassword(usernameToUse, codeToUse, password);
      navigate('/login?reset_password=1');
    } catch (error: any) {
      setError(error.message);
    }
  };

  // Password validation checks
  const passwordRecommendations = getPasswordRecommendations(password);
  const passwordStrength = getPasswordStrength(passwordRecommendations);

  return (
    <div className={styles.Login} data-testid="set new password">
      <section id="SetNewPasswordTitle" className={styles.LoginTitle}>
        <Typography variant="h1">Set new password</Typography>
      </section>

      <br/><br/><br/>
      <Box component="form" onSubmit={handleSubmit} sx={{ width: '80%', maxWidth: 500, textAlign: 'center' }}>
        <img src={KeyIcon} alt="key"/>
        <br/><br/>
        <Typography variant="h3" gutterBottom>Set new password</Typography>
        <br/><br/><br/>

        {/* Username Field */}
        {(!username) && (
          <Box sx={{ marginBottom: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ textAlign: 'left' }}>Username</Typography>
            <Input name="username" fullWidth disableUnderline placeholder="Enter your username" value={inputUsername} onChange={(e) => setInputUsername(e.target.value)}/>
          </Box>
        )}

        {/* Code Field */}
        {(!code) && (
          <Box sx={{ marginBottom: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ textAlign: 'left' }}>Reset password code</Typography>
            <Input name="code" fullWidth disableUnderline placeholder="Enter the 6 digits code from email" value={inputCode} onChange={(e) => setInputCode(e.target.value)}/>
          </Box>
        )}

        {/* Password Field */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
          <Typography variant="subtitle2" gutterBottom>Password</Typography>
          <Input name="password" fullWidth disableUnderline placeholder=""
                 value={password} onChange={(e) => setPassword(e.target.value)}
                 type={showPassword ? 'text' : 'password'}
                 endAdornment={
                   <InputAdornment position="end">
                     <IconButton
                       aria-label="toggle password visibility"
                       onClick={(e) => {e.preventDefault(); setShowPassword((show) => !show);}}
                       edge="end"
                       tabIndex={-1}
                     >
                       {showPassword ? <Visibility /> : <VisibilityOff />}
                     </IconButton>
                   </InputAdornment>
                 }
          />
          <Typography variant="caption">Password strength: {passwordStrength}</Typography>
          {password && (passwordRecommendations.filter(rec => !rec.valid).length > 0) && (
            <Box sx={{ marginTop: 0, textAlign: 'left' }}>
              <Typography variant="caption" gutterBottom>Recommendations to make your password stronger:</Typography>
              <ul>
                {passwordRecommendations.map((rec, index) => (
                  !rec.valid && <li key={index}>{rec.message}</li>
                ))}
              </ul>
            </Box>
          )}
        </Box>

        <button className={`PrimaryButton ${styles.LoginButton}`}>Reset password</button>
      </Box>
      <br/>
    </div>
  );
};

export default SetNewPassword;
